/**
 * Browser notifications for warning/advisory alerts. Respects the per-device mute preference.
 * Never used for anything safety-related — these are production heads-ups.
 */
import type { Alert } from '@apos/shared';
import { usePrefs } from './prefs';

export type NotifyPermission = NotificationPermission | 'unsupported';

export function notificationPermission(): NotifyPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotifyPermission> {
  if (notificationPermission() === 'unsupported') return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return notificationPermission();
  }
}

const shown = new Set<string>();

/** Show a system notification for an alert if permitted, unmuted and severe enough. */
export function notifyAlert(alert: Alert, missionName?: string): boolean {
  if (alert.severity === 'info') return false;
  if (usePrefs.getState().muteAlerts) return false;
  if (notificationPermission() !== 'granted') return false;
  if (shown.has(alert.id)) return false;
  shown.add(alert.id);
  if (shown.size > 500) shown.clear();
  try {
    const n = new Notification(alert.title, {
      body: missionName ? `${alert.body}\n${missionName}` : alert.body,
      tag: alert.dedupeKey,
      icon: '/icon-192.png',
      silent: alert.severity !== 'warning',
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Android Chrome requires ServiceWorkerRegistration.showNotification; ignore gracefully.
    return false;
  }
  if (alert.severity === 'warning' && 'vibrate' in navigator) {
    try {
      navigator.vibrate([120, 60, 120]);
    } catch {
      /* ignore */
    }
  }
  return true;
}
